import piexif from "piexifjs";

import MapsetLogo from "../images/mapset_logo.svg";
import NorthArrow from "../images/northArrow.svg";

import {
  EXTRA_DATA_COPYRIGHT_DEFAULT_FONTSIZE,
  EXTRA_DATA_COPYRIGHT_MIN_FONTSIZE,
  EXTRA_DATA_LOGO_MIN_HEIGHT,
  EXTRA_DATA_LOGO_MIN_WIDTH,
  EXTRA_DATA_NORTH_ARROW_MIN_SIZE,
  EXTRA_DATA_SIZE,
} from "./constants";
import { getDegreesFromRadians } from "./styleUtils";

export const getLogoExtraData = (scaleFactor, exportQuality) => {
  const aspectRatio = EXTRA_DATA_LOGO_MIN_HEIGHT / EXTRA_DATA_LOGO_MIN_WIDTH;
  let scaledHeight = EXTRA_DATA_SIZE * aspectRatio * scaleFactor;
  let scaledWidth = (EXTRA_DATA_SIZE + 1) * scaleFactor;
  if (scaledHeight < EXTRA_DATA_LOGO_MIN_HEIGHT) {
    scaledHeight = EXTRA_DATA_LOGO_MIN_HEIGHT;
  }
  if (scaledWidth < EXTRA_DATA_LOGO_MIN_WIDTH) {
    scaledWidth = EXTRA_DATA_LOGO_MIN_WIDTH;
  }
  return {
    height: scaledHeight * exportQuality,
    src: MapsetLogo,
    width: scaledWidth * exportQuality,
  };
};

export const getNorthArrowExtraData = (
  scaleFactor,
  exportQuality,
  rotation,
) => {
  // NorthArrow scales with export frame, minimum size is 30x30
  let northArrowScaledSize = EXTRA_DATA_SIZE * scaleFactor;
  if (northArrowScaledSize < EXTRA_DATA_NORTH_ARROW_MIN_SIZE) {
    northArrowScaledSize = EXTRA_DATA_NORTH_ARROW_MIN_SIZE;
  }
  const northArrowSize = northArrowScaledSize * exportQuality;
  return {
    height: northArrowSize,
    rotation: () => {
      return getDegreesFromRadians(rotation);
    },
    src: NorthArrow,
    width: northArrowSize,
  };
};

export const getCopyrightExtraData = (
  scaleFactor,
  text,
  exportQuality,
  maxWidth,
) => {
  let scaledFontSize = EXTRA_DATA_COPYRIGHT_DEFAULT_FONTSIZE * scaleFactor;
  if (scaledFontSize < EXTRA_DATA_COPYRIGHT_MIN_FONTSIZE) {
    scaledFontSize = EXTRA_DATA_COPYRIGHT_MIN_FONTSIZE;
  }
  return {
    background: true,
    font: `${Math.ceil(scaledFontSize) * exportQuality}px Arial`,
    maxWidth,
    paddingBottom: 5 * exportQuality,
    text,
  };
};

export const getQrCodeExtraData = (
  url,
  exportQrCodeWidth = EXTRA_DATA_SIZE,
  exportQuality,
) => {
  return {
    height: exportQrCodeWidth * exportQuality,
    src: url,
    width: exportQrCodeWidth * exportQuality,
  };
};

export function getDefaultDownloadImageName(format) {
  const fileExt = format === "image/jpeg" ? "jpg" : "png";
  return `${window.document.title.replace(/ /g, "_").toLowerCase()}.${fileExt}`;
}

async function addDPIToJPEGBlob(inputBlob, dpi = 300) {
  // Step 1: Convert Blob to Data URL
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      return resolve(reader.result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(inputBlob);
  });

  // Step 2: Prepare EXIF metadata for DPI
  const zeroth = {};
  zeroth[piexif.ImageIFD.XResolution] = [dpi, 1];
  zeroth[piexif.ImageIFD.YResolution] = [dpi, 1];
  zeroth[piexif.ImageIFD.ResolutionUnit] = 2; // 2 = Inches

  const exifObj = { "0th": zeroth };
  const exifBytes = piexif.dump(exifObj);

  // Step 3: Insert EXIF into JPEG data
  const newDataUrl = piexif.insert(exifBytes, dataUrl);

  // Step 4: Convert Data URL back to Blob
  const byteString = atob(newDataUrl.split(",")[1]);
  const mimeString = newDataUrl.split(",")[0].split(":")[1].split(";")[0];

  const arrayBuffer = new ArrayBuffer(byteString.length);
  const uint8Array = new Uint8Array(arrayBuffer);
  for (let i = 0; i < byteString.length; i += 1) {
    uint8Array[i] = byteString.charCodeAt(i);
  }

  return new Blob([arrayBuffer], { type: mimeString });
}

/* Function copied and modified from react-spatial
   https://github.com/geops/react-spatial/blob/master/src/components/CanvasSaveButton/CanvasSaveButton.js#L533
*/
export const downloadCanvasImage = (
  canvas,
  format,
  getDownloadImageName,
  dpi = 96,
) => {
  // Use blob for large images
  const promise = new Promise((resolve) => {
    if (/msie (9|10)/gi.test(window.navigator.userAgent.toLowerCase())) {
      // ie 9 and 10
      const url = canvas.toDataURL(format);
      const w = window.open("about:blank", "");
      w.document.write(`<img src="${url}" alt="from canvas"/>`);
      resolve(url);
    }
    if (window.navigator.msSaveBlob) {
      // ie 11 and higher
      let image;
      try {
        image = canvas.msToBlob();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e);
      }
      const blob = new Blob([image], {
        type: format,
      });
      resolve(blob);
      window.navigator.msSaveBlob(blob, getDownloadImageName(format));
    } else {
      canvas.toBlob(async (blob) => {
        let finalBlob = blob;
        // Set the metadata for jpg, see TRAFKLEIN-859
        if (finalBlob && format === "image/jpeg") {
          finalBlob = await addDPIToJPEGBlob(finalBlob, dpi);
        }
        const link = document.createElement("a");
        link.download = getDownloadImageName(format);
        link.href = URL.createObjectURL(finalBlob);
        // append child to document for firefox to be able to download.
        document.body.appendChild(link);
        link.click();
        resolve(finalBlob);
      }, format);
    }
  });
  return promise;
};
